import React, {useCallback, useContext, useEffect, useRef, useState, createContext} from "react";
import {useEvents} from "../../lib/useEvents";
import {Layout} from "../../pages/siren/Layout";
import {platformAPI} from "../../api/axiosConfig";
import {IconButton} from "./import/inputs";
import SaveIcon from "@mui/icons-material/Save";
import {SelectList, SelectListHandle, SelectListItem, SelectListPage} from "../../components/select-list/SelectList";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublicIcon from "@mui/icons-material/Public";

type SirenDriver = {
    id: string;
    driver: string;
    type: string;
    enabled: boolean;
    region: string;
    allowedRegions: string[];
    allowedRecipients: string[];
    from: string;
    credentialKeys: string[];
    credentialPreviews: Record<string, string>;
    options: Record<string, unknown>;
    clientID: string | null;
};

type FormState = {
    driver: string;
    type: string;
    region: string;
    allowedRegions: string[];
    allowedRecipients: string[];
    from: string;
    credentials: Record<string, string>;
    credentialPreviews: Record<string, string>;
    options: Record<string, string>;
};

const emptyForm = (): FormState => ({
    driver: "twilio_sms",
    type: "sms",
    region: "",
    allowedRegions: [],
    allowedRecipients: [],
    from: "",
    credentials: {},
    credentialPreviews: {},
    options: {},
});

const DRIVER_TYPES = ["sms", "call", "email", "app"];

const DRIVERS_BY_TYPE: Record<string, string[]> = {
    sms:   ["twilio_sms", "twilio_sms_messaging_service", "smspit_sms", "log_sms"],
    call:  ["twilio_call", "log_call"],
    email: ["mailgun_email", "ses_email", "smtp_email", "log_email"],
    app:   ["fcm", "log_app"],
};

type FieldDef = { key: string; label: string; placeholder?: string; password?: boolean; textarea?: boolean; options?: string[] };

const FCM_CREDENTIAL_FIELDS: FieldDef[] = [
    {key: "type", label: "Type"},
    {key: "project_id", label: "Project ID"},
    {key: "private_key_id", label: "Private Key ID"},
    {key: "client_email", label: "Client Email"},
    {key: "client_id", label: "Client ID"},
    {key: "auth_uri", label: "Auth URI"},
    {key: "token_uri", label: "Token URI"},
    {key: "auth_provider_x509_cert_url", label: "Auth Provider Cert URL"},
    {key: "client_x509_cert_url", label: "Client Cert URL"},
    {key: "universe_domain", label: "Universe Domain"},
    {key: "private_key", label: "Private Key", textarea: true, placeholder: "-----BEGIN RSA PRIVATE KEY-----\n..."},
];

const CREDENTIAL_FIELDS: Record<string, FieldDef[]> = {
    twilio_sms:                   [{key: "sid", label: "Account SID"}, {key: "secret", label: "Auth Token", password: true}],
    twilio_sms_messaging_service: [{key: "sid", label: "Account SID"}, {key: "secret", label: "Auth Token", password: true}, {key: "messagingServiceSID", label: "Messaging Service SID"}],
    twilio_call:                  [{key: "sid", label: "Account SID"}, {key: "secret", label: "Auth Token", password: true}],
    smspit_sms:                   [{key: "baseUrl", label: "Base URL", placeholder: "https://..."}, {key: "provider", label: "Provider"}],
    mailgun_email:                [{key: "apiKey", label: "API Key", password: true}, {key: "domain", label: "Domain", placeholder: "mg.example.com"}, {key: "region", label: "Region", placeholder: "us"}],
    ses_email:                    [{key: "accessKeyId", label: "Access Key ID"}, {key: "secretAccessKey", label: "Secret Access Key", password: true}, {key: "region", label: "Region", placeholder: "eu-west-2"}],
    smtp_email:                   [{key: "host", label: "Host"}, {key: "port", label: "Port", placeholder: "587"}, {key: "username", label: "Username"}, {key: "password", label: "Password", password: true}],
    fcm:                          FCM_CREDENTIAL_FIELDS,
    log_sms:                      [],
    log_call:                     [],
    log_email:                    [],
    log_app:                      [],
};

const SEND_TIMEOUT: FieldDef = {key: "sendTimeoutSeconds", label: "Send Timeout (seconds)", placeholder: "default"};
const USE_FOR_OPT_IN: FieldDef = {key: "useForOptIn", label: "Use For Opt-in", options: ["", "true", "false"]};
const SMS_SENDER_ID: FieldDef = {key: "smsSenderID", label: "SMS Sender ID", options: ["", "alphanumeric", "long_code"]};
const POLL_DELIVERY: FieldDef = {key: "pollDelivery", label: "Poll Delivery", options: ["", "true", "false"]};
const SES_CONFIGURATION_SET: FieldDef = {key: "configurationSet", label: "Configuration Set", placeholder: "siren-prod"};

const BOOLEAN_OPTION_KEYS = new Set(["useForOptIn", "pollDelivery"]);

const NON_SENSITIVE_CREDENTIAL_KEYS = new Set([
    "sid", "messagingServiceSID", "baseUrl", "provider",
    "host", "port", "username", "domain", "region", "accessKeyId",
]);

function serializeOptions(opts: Record<string, string>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(opts)) {
        if (BOOLEAN_OPTION_KEYS.has(k)) {
            if (v === "") continue;
            result[k] = v === "true";
        } else {
            result[k] = v;
        }
    }
    return result;
}

const OPTION_FIELDS: Record<string, FieldDef[]> = {
    twilio_sms:                   [USE_FOR_OPT_IN, SMS_SENDER_ID, SEND_TIMEOUT],
    twilio_sms_messaging_service: [USE_FOR_OPT_IN, SMS_SENDER_ID, SEND_TIMEOUT],
    smspit_sms:                   [USE_FOR_OPT_IN, SMS_SENDER_ID, SEND_TIMEOUT],
    twilio_call:                  [SEND_TIMEOUT],
    mailgun_email:                [POLL_DELIVERY, SEND_TIMEOUT],
    ses_email:                    [SES_CONFIGURATION_SET, SEND_TIMEOUT],
    smtp_email:                   [SEND_TIMEOUT],
    fcm:                          [SEND_TIMEOUT],
};

function TypeBadge({type}: {type: string}) {
    const styles: Record<string, string> = {
        sms:   "bg-light-blue text-dark-blue",
        call:  "bg-desaturated-green text-dark-green",
        email: "bg-light-orange text-dark-orange",
        app:   "bg-desaturated-grey text-grey",
    };
    const cls = styles[type] ?? "bg-desaturated-grey text-grey";
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{type}</span>;
}

function EnabledBadge({enabled}: {enabled: boolean}) {
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${enabled ? "bg-desaturated-green text-dark-green" : "bg-desaturated-grey text-grey"}`}>
            {enabled ? "Enabled" : "Disabled"}
        </span>
    );
}

function FieldsEditor({label, fields, value, onChange, previews}: {
    label: string;
    fields: FieldDef[];
    value: Record<string, string>;
    onChange: (v: Record<string, string>) => void;
    previews?: Record<string, string>;
}) {
    if (fields.length === 0) return null;
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-baseline gap-3">
                <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">{label}</p>
                {previews && fields.some(f => !NON_SENSITIVE_CREDENTIAL_KEYS.has(f.key)) && <p className="text-xs text-light-grey">Leave sensitive fields blank to keep existing values</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
                {fields.map(f => {
                    const preview = previews?.[f.key];
                    const placeholder = (!NON_SENSITIVE_CREDENTIAL_KEYS.has(f.key) && preview) ? preview : (f.placeholder ?? "");
                    return (
                        <div key={f.key} className="flex flex-col gap-1">
                            <label className="font-medium text-sm">{f.label}</label>
                            {f.options ? (
                                <select
                                    className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                                    value={value[f.key] ?? ""}
                                    onChange={e => onChange({...value, [f.key]: e.target.value})}
                                >
                                    {f.options.map(o => <option key={o} value={o}>{o === "" ? "— not set —" : o}</option>)}
                                </select>
                            ) : f.textarea ? (
                                <textarea
                                    className="shadow-md rounded-md px-3 py-2 outline-none bg-white text-sm font-mono resize-y"
                                    rows={5}
                                    placeholder={placeholder}
                                    value={value[f.key] ?? ""}
                                    onChange={e => onChange({...value, [f.key]: e.target.value})}
                                />
                            ) : (
                                <input
                                    className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm font-mono"
                                    type={f.password ? "password" : "text"}
                                    placeholder={placeholder}
                                    value={value[f.key] ?? ""}
                                    onChange={e => onChange({...value, [f.key]: e.target.value})}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function RegionsEditor({value, onChange}: {value: string[]; onChange: (v: string[]) => void}) {
    const [input, setInput] = useState("");
    const add = () => {
        const v = input.trim().toUpperCase();
        if (!v || value.includes(v)) return;
        onChange([...value, v]);
        setInput("");
    };
    return (
        <div className="flex flex-col gap-2">
            <label className="font-medium text-sm">Allowed Regions (ISO)</label>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {value.map(r => (
                        <span key={r} className="inline-flex items-center gap-1 bg-light-light-grey text-sm rounded-full px-3 py-1 font-mono">
                            {r}
                            <button type="button" className="ml-1 text-light-grey hover:text-red" onClick={() => onChange(value.filter(x => x !== r))}>×</button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <input
                    className="flex-1 h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm font-mono"
                    placeholder="e.g. AU"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                />
                <button
                    type="button"
                    className="h-9 px-3 bg-blue text-white rounded-md shadow-md flex items-center text-sm hover:bg-opacity-90 disabled:opacity-50"
                    disabled={!input.trim()}
                    onClick={add}
                >
                    <AddIcon style={{fontSize: 16}}/>
                </button>
            </div>
        </div>
    );
}

function WhitelistEditor({value, onChange, type}: {value: string[]; onChange: (v: string[]) => void; type: string}) {
    const [text, setText] = useState(value.join("\n"));
    const handleBlur = () => {
        const entries = text.split("\n").map(s => s.trim()).filter(Boolean);
        onChange(entries);
    };
    const placeholder = type === "email"
        ? "e.g.\nalice@example.com\nbob@example.com"
        : "e.g.\n+447700000001\n+447700000002";
    return (
        <div className="flex flex-col gap-2">
            <label className="font-medium text-sm">Allowed Recipients</label>
            <textarea
                className="shadow-md rounded-md px-3 py-2 outline-none bg-white text-sm font-mono resize-y"
                rows={4}
                placeholder={placeholder}
                value={text}
                onChange={e => setText(e.target.value)}
                onBlur={handleBlur}
            />
            <p className="text-xs text-light-grey">One phone number or email per line. Leave empty to allow all (whitelist disabled).</p>
        </div>
    );
}

function driverToForm(d: SirenDriver): FormState {
    const opts: Record<string, string> = {};
    Object.entries(d.options ?? {}).forEach(([k, v]) => { opts[k] = String(v); });
    const credentials: Record<string, string> = {};
    Object.entries(d.credentialPreviews ?? {}).forEach(([k, v]) => {
        if (NON_SENSITIVE_CREDENTIAL_KEYS.has(k)) credentials[k] = v;
    });
    return {
        driver: d.driver,
        type: d.type,
        region: d.region,
        allowedRegions: d.allowedRegions ?? [],
        allowedRecipients: d.allowedRecipients ?? [],
        from: d.from,
        credentials,
        credentialPreviews: d.credentialPreviews ?? {},
        options: opts,
    };
}

function DriverForm({
    initial,
    isEditing = false,
    copyCredentials,
    onCopyCredentialsChange,
    duplicateHasCredentials,
    onSave,
    onDelete,
    saving,
    error,
}: {
    initial: FormState;
    isEditing?: boolean;
    copyCredentials?: boolean;
    onCopyCredentialsChange?: (v: boolean) => void;
    duplicateHasCredentials?: boolean;
    onSave: (f: FormState) => void;
    onDelete?: () => void;
    saving: boolean;
    error: string | null;
}) {
    const [form, setForm] = useState<FormState>(initial);
    const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => ({...f, [k]: v}));
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleTypeChange = (newType: string) => {
        const options = DRIVERS_BY_TYPE[newType] ?? [];
        setForm(f => ({...f, type: newType, driver: options[0] ?? ""}));
    };

    const handleFCMJsonFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const json = JSON.parse(ev.target?.result as string);
                const creds: Record<string, string> = {};
                for (const field of FCM_CREDENTIAL_FIELDS) {
                    if (json[field.key] !== undefined) {
                        creds[field.key] = String(json[field.key]);
                    }
                }
                setForm(f => ({...f, credentials: {...f.credentials, ...creds}}));
            } catch {
                // ignore parse errors
            }
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    return (
        <div className="flex flex-col gap-4">
            {/* General */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-4">
                <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">General</p>
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="font-medium text-sm">Type</label>
                        {isEditing ? (
                            <div className="h-9 shadow-md rounded-md px-3 flex items-center bg-light-light-grey text-sm text-grey font-mono">{form.type}</div>
                        ) : (
                            <select
                                className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                                value={form.type}
                                onChange={e => handleTypeChange(e.target.value)}
                            >
                                {DRIVER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-medium text-sm">Driver</label>
                        {isEditing ? (
                            <div className="h-9 shadow-md rounded-md px-3 flex items-center bg-light-light-grey text-sm text-grey font-mono">{form.driver}</div>
                        ) : (
                            <select
                                className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm font-mono"
                                value={form.driver}
                                onChange={e => set("driver", e.target.value)}
                            >
                                {(DRIVERS_BY_TYPE[form.type] ?? []).map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        )}
                    </div>
                    {form.driver !== "fcm" && form.type !== "email" && (
                        <div className="flex flex-col gap-1">
                            <label className="font-medium text-sm">Primary Region (ISO)</label>
                            <input
                                className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm font-mono"
                                placeholder="e.g. AU"
                                value={form.region}
                                onChange={e => set("region", e.target.value.toUpperCase())}
                            />
                        </div>
                    )}
                    {form.driver !== "fcm" && (
                        <div className="flex flex-col gap-1">
                            <label className="font-medium text-sm">From</label>
                            <input
                                className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm font-mono"
                                placeholder="e.g. +61400000000 or sender name"
                                value={form.from}
                                onChange={e => set("from", e.target.value)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Credentials */}
            {CREDENTIAL_FIELDS[form.driver]?.length > 0 && (
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-4">
                    {!isEditing && duplicateHasCredentials && onCopyCredentialsChange && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={!!copyCredentials}
                                onChange={e => onCopyCredentialsChange(e.target.checked)}
                            />
                            Copy credentials from parent driver
                        </label>
                    )}
                    {!copyCredentials && (
                        <FieldsEditor
                            label="Credentials"
                            fields={CREDENTIAL_FIELDS[form.driver] ?? []}
                            value={form.credentials}
                            onChange={v => set("credentials", v)}
                            previews={isEditing ? form.credentialPreviews : undefined}
                        />
                    )}
                    {form.driver === "fcm" && (
                        <>
                            <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleFCMJsonFile}/>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    className="h-8 px-3 text-sm rounded-md border border-light-grey hover:bg-light-light-grey"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Load from JSON file
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Options */}
            {OPTION_FIELDS[form.driver]?.length > 0 && (
                <div className="bg-white shadow-md rounded-lg p-4">
                    <FieldsEditor
                        label="Options"
                        fields={OPTION_FIELDS[form.driver] ?? []}
                        value={form.options}
                        onChange={v => set("options", v)}
                    />
                </div>
            )}

            {/* Restrictions */}
            {form.driver !== "fcm" && form.type !== "email" && (
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-4">
                    <RegionsEditor value={form.allowedRegions} onChange={v => set("allowedRegions", v)}/>
                    <WhitelistEditor value={form.allowedRecipients} onChange={v => set("allowedRecipients", v)} type={form.type}/>
                </div>
            )}

            {error && <p className="text-sm text-red">{error}</p>}

            <div className="flex gap-2 justify-end">
                {onDelete && (
                    <IconButton text="Delete" icon={<DeleteIcon style={{fontSize: 16}}/>} color="red" className="w-32" onClick={onDelete}/>
                )}
                <IconButton text={isEditing ? "Update" : "Create"} icon={isEditing ? <SaveIcon style={{fontSize: 16}}/> : <AddIcon style={{fontSize: 16}}/>} className="w-32" onClick={() => onSave(form)} loading={saving} disabled={!form.driver || !form.type}/>
            </div>
        </div>
    );
}

const CREATE_NEW_ID = "__create__";

const TogglesEnabledContext = createContext(false);

function DriverListLabel({driver, onToggle, onDuplicate}: {
    driver: SirenDriver;
    onToggle: (id: string, enabled: boolean, e: React.MouseEvent) => void;
    onDuplicate: (d: SirenDriver, e: React.MouseEvent) => void;
}) {
    const togglesEnabled = useContext(TogglesEnabledContext);
    const {listen} = useEvents();
    const [enabled, setEnabled] = useState(driver.enabled);

    useEffect(() => {
        return listen("siren:driver:changed", ({id, enabled: newEnabled}) => {
            if (id === driver.id) setEnabled(!!newEnabled);
        });
    }, [driver.id, listen]);

    return (
        <span className="flex items-center gap-2 w-full">
            <TypeBadge type={driver.type}/>
            <span className="font-mono text-sm flex-1 min-w-0 truncate">{driver.driver}</span>
            <button
                type="button"
                onClick={e => onDuplicate(driver, e)}
                title="Duplicate"
                className="shrink-0 text-light-grey hover:text-blue"
            >
                <ContentCopyIcon style={{fontSize: 14}}/>
            </button>
            <button
                type="button"
                onClick={e => onToggle(driver.id, enabled, e)}
                title={togglesEnabled ? undefined : "Unlock to toggle"}
                className={`shrink-0 ${togglesEnabled ? "cursor-pointer" : "cursor-default opacity-60"}`}
            >
                <EnabledBadge enabled={enabled}/>
            </button>
        </span>
    );
}

// --- Driver panel (loads + saves a single driver) ---

const DriverPanel: React.FC<{
    driverID: string | null;
    clientID: string | null;
    initialForm?: FormState;
    copyCredentialsFromID?: string;
    duplicateHasCredentials?: boolean;
    onSaved: (id: string) => void;
    onDeleted: () => void;
}> = ({driverID, clientID, initialForm, copyCredentialsFromID, duplicateHasCredentials, onSaved, onDeleted}) => {
    const [driver, setDriver] = useState<SirenDriver | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copyCredentials, setCopyCredentials] = useState(!!copyCredentialsFromID);

    useEffect(() => {
        if (!driverID) return;
        setLoading(true);
        setError(null);
        platformAPI.get<SirenDriver>(`api/siren/drivers/${driverID}`)
            .then(res => setDriver(res.data))
            .catch(() => setError("Failed to load driver"))
            .finally(() => setLoading(false));
    }, [driverID]);

    const handleSave = useCallback(async (form: FormState) => {
        setSaving(true);
        setError(null);
        try {
            const body: Record<string, unknown> = {
                driver: form.driver,
                type: form.type,
                region: form.region,
                allowedRegions: form.allowedRegions,
                allowedRecipients: form.allowedRecipients,
                from: form.from,
                credentials: form.credentials,
                options: serializeOptions(form.options),
                clientID: clientID || null,
            };
            if (!driverID && copyCredentials && copyCredentialsFromID) {
                body.copyCredentialsFromID = copyCredentialsFromID;
            }
            if (driverID) {
                await platformAPI.put(`api/siren/drivers/${driverID}`, body);
                onSaved(driverID);
            } else {
                const res = await platformAPI.post<SirenDriver>("api/siren/drivers", body);
                onSaved(res.data.id);
            }
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Error saving driver");
        } finally {
            setSaving(false);
        }
    }, [driverID, clientID, onSaved, copyCredentials, copyCredentialsFromID]);

    const handleDelete = useCallback(async () => {
        if (!driverID) return;
        setDeleting(true);
        try {
            await platformAPI.delete(`api/siren/drivers/${driverID}`);
            onDeleted();
        } catch {
            setDeleting(false);
            setConfirmDelete(false);
        }
    }, [driverID, onDeleted]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <>
            <DriverForm
                initial={driver ? driverToForm(driver) : (initialForm ?? emptyForm())}
                isEditing={!!driverID}
                copyCredentials={copyCredentials}
                onCopyCredentialsChange={setCopyCredentials}
                duplicateHasCredentials={duplicateHasCredentials}
                onSave={handleSave}
                onDelete={driverID ? () => setConfirmDelete(true) : undefined}
                saving={saving}
                error={error}
            />

            {confirmDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete Driver</h3>
                        <p className="text-sm">Delete driver <span className="font-mono font-semibold">{driver?.driver}</span>?</p>
                        <div className="flex gap-2 justify-end">
                            <button
                                className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey"
                                onClick={() => setConfirmDelete(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50"
                                disabled={deleting}
                                onClick={handleDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// --- Client+Driver split selector (mirrors ScopeSplitSelector pattern) ---

type ClientDriverSelectorProps = {
    selectedDriverID: string | null;
    onSelectDriver: (driverID: string, clientID: string | null) => void;
    onAddDriver: (clientID: string | null) => void;
    togglesEnabled: boolean;
    togglesEnabledRef: React.MutableRefObject<boolean>;
    onToggleEnabled: (v: boolean) => void;
    listRevision: number;
};

const ClientDriverSelector: React.FC<ClientDriverSelectorProps> = ({
    selectedDriverID, onSelectDriver, onAddDriver, togglesEnabled, togglesEnabledRef, onToggleEnabled, listRevision,
}) => {
    const [selectedClient, setSelectedClient] = useState<{id: string; name: string} | null>(null);
    const {emit} = useEvents();

    const PAGE_SIZE = 12;

    const getClients = useCallback(async (search: string, page: number): Promise<SelectListPage<{id: string; name: string}>> => {
        try {
            const res = await platformAPI.get<{items: {id: string; name: string}[]; totalPages: number}>("api/clients", {
                params: {search, page: page + 1, limit: PAGE_SIZE, module: "siren"},
            });
            return {
                items: res.data.items.map(c => ({item: c, label: c.name})),
                totalPages: Math.max(1, res.data.totalPages),
            };
        } catch {
            return {items: [], totalPages: 1};
        }
    }, []);

    const handleDuplicate = useCallback((d: SirenDriver, e: React.MouseEvent) => {
        e.stopPropagation();
        onAddDriver(selectedClient?.id ?? null);
        // pass duplicate form via event — handled in parent
        emit("siren:driver:duplicate", {form: driverToForm(d), sourceID: d.id, hasCredentials: d.credentialKeys.length > 0});
    }, [selectedClient, onAddDriver, emit]);

    const toggleEnabled = useCallback(async (id: string, currentEnabled: boolean, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!togglesEnabledRef.current) return;
        const newEnabled = !currentEnabled;
        emit("siren:driver:changed", {id, enabled: newEnabled});
        try {
            await platformAPI.patch(`api/siren/drivers/${id}/enabled`, {enabled: newEnabled});
        } catch {
            emit("siren:driver:changed", {id, enabled: currentEnabled});
        }
    }, [emit, togglesEnabledRef]);

    const getDrivers = useCallback(async (search: string, page: number): Promise<SelectListPage<SirenDriver>> => {
        try {
            const params: Record<string, unknown> = {search, page: page + 1, limit: PAGE_SIZE};
            if (selectedClient) params.clientID = selectedClient.id;
            const res = await platformAPI.get<{results: SirenDriver[]; totalResults: number; totalPages: number}>("api/siren/drivers", {params});
            return {
                items: res.data.results.map(d => ({
                    item: d,
                    label: <DriverListLabel driver={d} onToggle={toggleEnabled} onDuplicate={handleDuplicate}/>,
                    sublabel: <span className="flex items-center gap-1">{[d.region, d.from].filter(Boolean).join(" · ")}{d.allowedRecipients?.length > 0 && <LockOutlinedIcon style={{fontSize: 12}} titleAccess="Whitelist active"/>}</span>,
                })),
                totalPages: Math.max(1, res.data.totalPages),
            };
        } catch {
            return {items: [], totalPages: 1};
        }
    }, [selectedClient, toggleEnabled, handleDuplicate]);

    if (!selectedClient) {
        return (
            <SelectList<{id: string; name: string}>
                getItems={getClients}
                selectedID={null}
                pageSize={PAGE_SIZE}
                onSelect={c => setSelectedClient(c)}
                header={
                    <div className="border-b border-light-light-grey">
                        <button
                            type="button"
                            className="flex items-center gap-1.5 w-full px-4 py-3 text-left text-sm hover:bg-light-light-grey"
                            onClick={() => setSelectedClient({id: "", name: "Global"})}
                        >
                            <PublicIcon style={{fontSize: 16}}/>
                            Global
                        </button>
                    </div>
                }
            />
        );
    }

    const isGlobal = selectedClient.id === "";
    const clientID = isGlobal ? null : selectedClient.id;

    const driverListHeader = (
        <div className="flex flex-col border-b border-light-light-grey">
            <button
                className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-blue hover:bg-light-light-grey transition-colors border-b border-light-light-grey"
                onClick={() => { setSelectedClient(null); onSelectDriver("", null); }}
            >
                <ArrowBackIcon style={{fontSize: 16}}/>
                <span>Clients</span>
                <span className="text-light-grey font-normal">/ {selectedClient.name}</span>
            </button>
            <div className="flex border-b border-light-light-grey">
                <button
                    type="button"
                    className="flex-1 px-4 py-2.5 text-left text-sm flex items-center gap-1.5 hover:bg-light-light-grey"
                    onClick={() => onAddDriver(clientID)}
                >
                    <AddCircleOutlineIcon style={{fontSize: 16}}/>Add Driver
                </button>
                <button
                    type="button"
                    title={togglesEnabled ? "Lock enabled toggles" : "Unlock enabled toggles"}
                    className={`px-3 flex items-center hover:bg-light-light-grey ${togglesEnabled ? "text-blue" : "text-light-grey"}`}
                    onClick={() => { const next = !togglesEnabled; togglesEnabledRef.current = next; onToggleEnabled(next); }}
                >
                    {togglesEnabled ? <LockOpenOutlinedIcon style={{fontSize: 18}}/> : <LockOutlinedIcon style={{fontSize: 18}}/>}
                </button>
            </div>
        </div>
    );

    return (
        <SelectList<SirenDriver>
            key={`${selectedClient.id}-${listRevision}`}
            getItems={getDrivers}
            selectedID={selectedDriverID === CREATE_NEW_ID ? null : selectedDriverID}
            onSelect={d => onSelectDriver(d.id, clientID)}
            pageSize={PAGE_SIZE}
            hasSublabel={true}
            header={driverListHeader}
        />
    );
};

// --- Main page ---

export const SirenDriversPage: React.FC = () => {
    const [selectedDriverID, setSelectedDriverID] = useState<string | null>(null);
    const [selectedClientID, setSelectedClientID] = useState<string | null>(null);
    const [duplicateForm, setDuplicateForm] = useState<FormState | null>(null);
    const [duplicateSourceID, setDuplicateSourceID] = useState<string | null>(null);
    const [duplicateHasCredentials, setDuplicateHasCredentials] = useState(false);
    const [togglesEnabled, setTogglesEnabled] = useState(false);
    const togglesEnabledRef = useRef(false);
    const [listRevision, setListRevision] = useState(0);
    const {listen} = useEvents();

    useEffect(() => {
        return listen("siren:driver:duplicate", (detail) => {
            setDuplicateForm(detail.form as FormState);
            setDuplicateSourceID((detail.sourceID as string) ?? null);
            setDuplicateHasCredentials(!!(detail.hasCredentials));
        });
    }, [listen]);

    const handleSelectDriver = useCallback((driverID: string, clientID: string | null) => {
        setSelectedDriverID(driverID || null);
        setSelectedClientID(clientID);
        setDuplicateForm(null);
        setDuplicateSourceID(null);
        setDuplicateHasCredentials(false);
    }, []);

    const handleAddDriver = useCallback((clientID: string | null) => {
        setSelectedClientID(clientID);
        setSelectedDriverID(CREATE_NEW_ID);
        setDuplicateForm(null);
        setDuplicateSourceID(null);
        setDuplicateHasCredentials(false);
    }, []);

    const panelDriverID = selectedDriverID === CREATE_NEW_ID ? null : selectedDriverID;

    return (
        <TogglesEnabledContext.Provider value={togglesEnabled}>
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <ClientDriverSelector
                        selectedDriverID={selectedDriverID}
                        onSelectDriver={handleSelectDriver}
                        onAddDriver={handleAddDriver}
                        togglesEnabled={togglesEnabled}
                        togglesEnabledRef={togglesEnabledRef}
                        onToggleEnabled={v => { togglesEnabledRef.current = v; setTogglesEnabled(v); }}
                        listRevision={listRevision}
                    />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {!selectedDriverID && (
                        <div className="p-4 text-sm text-light-grey">Select a client to view its drivers, or add a new one.</div>
                    )}
                    {selectedDriverID && (
                        <DriverPanel
                            key={selectedDriverID}
                            driverID={panelDriverID}
                            clientID={selectedClientID}
                            initialForm={!panelDriverID ? (duplicateForm ?? undefined) : undefined}
                            copyCredentialsFromID={!panelDriverID ? (duplicateSourceID ?? undefined) : undefined}
                            duplicateHasCredentials={!panelDriverID ? duplicateHasCredentials : false}
                            onSaved={id => { setListRevision(r => r + 1); setSelectedDriverID(id); setDuplicateForm(null); setDuplicateSourceID(null); setDuplicateHasCredentials(false); }}
                            onDeleted={() => { setListRevision(r => r + 1); setSelectedDriverID(null); }}
                        />
                    )}
                </div>
            </div>
        </Layout>
        </TogglesEnabledContext.Provider>
    );
};
